import {
  backoffMs,
  groupByKit,
  httpStatusOf,
  isRetryable,
  outboxReason,
  pendingOf,
  type OutboxEntry,
} from '@/domain';
import type {
  CompleteWorkCommand,
  FlagCommand,
  KitRepository,
  QcReworkCommand,
  StartWorkCommand,
} from '@/application/ports/KitRepository';
import type { OutboxQueue } from '@/application/ports/OutboxQueue';

/**
 * Replays the outbox. Section 9 rules 3, 5 and 6.
 *
 * Order is preserved per kit and kits go in parallel, because start then
 * complete advances a stage while complete then start is a 409 and a stuck
 * queue, and because two kits have nothing to say to each other.
 *
 * A duplicated replay is harmless: every entry carries its `Idempotency-Key`
 * and the server returns the stored response for a repeat. That is what makes
 * it safe to retry on reconnect, on focus and on a timer all at once.
 */

/** Turns a stored entry back into the call that made it. */
async function send(repo: KitRepository, entry: OutboxEntry): Promise<void> {
  switch (entry.kind) {
    case 'start':
      await repo.start(entry.payload as StartWorkCommand);
      return;
    case 'complete':
      await repo.complete(entry.payload as CompleteWorkCommand);
      return;
    case 'flag':
      await repo.flag(entry.payload as FlagCommand);
      return;
    case 'qcRework':
      await repo.qcRework(entry.payload as QcReworkCommand);
      return;
    case 'note': {
      const { text, visibleToUserId } = entry.payload as {
        text: string;
        visibleToUserId?: string | null;
      };
      await repo.addNote(entry.kitId, text, entry.id, visibleToUserId ?? null);
      return;
    }
    case 'photo': {
      const { file } = entry.payload as { file: File };
      await repo.addPhoto(entry.kitId, file, entry.id);
      return;
    }
    case 'workProof': {
      // The entry id is the Idempotency-Key the page used, so a replay of an
      // upload the server already took is a no-op rather than a second proof.
      const { file, note } = entry.payload as { file: File; note?: string };
      await repo.addWorkProof(entry.kitId, file, entry.id, note);
      return;
    }
  }
}

export interface ReplayResult {
  sent: number;
  stillPending: number;
  failed: number;
}

export class OutboxReplay {
  /**
   * When each entry is next worth attempting. Held here rather than on the
   * entry because the stored shape is the one the spec fixes, and a backoff
   * clock is this driver's business rather than the record's.
   */
  private readonly nextAttemptAt = new Map<string, number>();
  private inFlight: Promise<ReplayResult> | null = null;

  constructor(
    private readonly queue: OutboxQueue,
    private readonly repo: KitRepository,
  ) {}

  /**
   * One pass over everything pending.
   *
   * Reconnect, focus and the interval can all fire within the same second, so
   * a pass already running is joined rather than started again. Without this a
   * phone coming back into range sends every queued command three times; the
   * idempotency key makes that harmless but it is still three times the
   * traffic from a building full of phones.
   */
  run(now: number = Date.now()): Promise<ReplayResult> {
    this.inFlight ??= this.pass(now).finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async pass(now: number): Promise<ReplayResult> {
    const pending = pendingOf(await this.queue.all());
    if (pending.length === 0) return { sent: 0, stillPending: 0, failed: 0 };

    const result: ReplayResult = { sent: 0, stillPending: 0, failed: 0 };

    await Promise.all(
      groupByKit(pending).map(async (group) => {
        for (const entry of group) {
          const due = this.nextAttemptAt.get(entry.id) ?? 0;
          if (now < due) {
            result.stillPending += 1;
            // Everything behind it on this kit waits too, or the order breaks.
            return;
          }

          try {
            await send(this.repo, entry);
            await this.queue.markSent(entry.id);
            this.nextAttemptAt.delete(entry.id);
            result.sent += 1;
          } catch (error) {
            const retryable = isRetryable(httpStatusOf(error));
            await this.queue.markFailed(
              entry.id,
              outboxReason(error),
              retryable,
            );

            if (retryable) {
              this.nextAttemptAt.set(entry.id, now + backoffMs(entry.attempts + 1));
              result.stillPending += 1;
            } else {
              this.nextAttemptAt.delete(entry.id);
              result.failed += 1;
            }

            // Stop this kit for this pass either way. Sending the next command
            // when the one before it did not land is how a bench ends up with a
            // kit the server thinks is somewhere else.
            return;
          }
        }
      }),
    );

    return result;
  }
}
